import React from 'react';
import { motion } from 'motion/react';
import { Clock, Calendar, Calculator, MapPin, Mail, Keyboard } from 'lucide-react';

const MagicShortcuts = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const shortcuts = [
    { 
      trigger: ".time", 
      result: "Inserts current time",
      example: "3:45 PM",
      icon: Clock
    },
    { 
      trigger: ".date", 
      result: "Inserts today's date",
      example: "14 Mar 2026",
      icon: Calendar
    },
    { 
      trigger: "23x45=", 
      result: "Calculator answer",
      example: "1035",
      icon: Calculator
    },
    { 
      trigger: ".addr", 
      result: "Your full address",
      example: "(custom)",
      icon: MapPin
    },
    { 
      trigger: ".mail", 
      result: "Your email address",
      example: "(custom)",
      icon: Mail
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={springConfig}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 px-4 py-2 rounded-full mb-4">
            <span>⚡</span>
            <span className="text-sm font-medium">Magic Shortcuts</span>
          </div>
          <h2 
            className="heading-large mb-4"
            style={{
              fontSize: 'clamp(32px, 4vw, 48px)',
              fontWeight: 600,
              color: '#0A0A0A',
              letterSpacing: '-0.01em'
            }}
          >
            Type Less. Do More.
          </h2>
          <p 
            className="body-large max-w-2xl mx-auto"
            style={{
              fontSize: '18px',
              color: '#3C3B3D',
              lineHeight: 1.6
            }}
          >
            Built-in smart shortcuts that save you time every single day. 
            Plus create your own custom expansions.
          </p>
        </motion.div>

        {/* Shortcuts Grid */}
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {shortcuts.map((shortcut, index) => {
              const Icon = shortcut.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-5 border border-gray-200 hover:border-orange-300 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 107, 157, 0.1) 100%)'
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: '#FF6B9D' }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <code 
                          className="px-2 py-1 rounded text-sm font-mono font-bold"
                          style={{ 
                            background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                            color: 'white'
                          }}
                        >
                          {shortcut.trigger}
                        </code>
                        <span className="text-gray-400">→</span>
                      </div>
                      <p className="text-sm text-gray-700">{shortcut.result}</p>
                      <p className="text-xs text-gray-500 italic">{shortcut.example}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Additional Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Inline Calculator */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.3 }}
              className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-6 border border-orange-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <Calculator className="w-6 h-6" style={{ color: '#FF6B9D' }} />
                <h3 className="font-semibold text-lg" style={{ color: '#0A0A0A' }}>Inline Calculator</h3>
              </div>
              <div className="bg-white rounded-xl p-4 mb-3">
                <code className="text-xl font-mono">
                  <span className="text-gray-600">100/4=</span>
                  <span className="font-bold" style={{ color: '#FF6B9D' }}>25</span>
                </code>
              </div>
              <p className="text-sm text-gray-600">
                Supports <strong>+ − × ÷</strong> and <strong>x</strong> for multiply
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Just type the expression and press Space
              </p>
            </motion.div>

            {/* Long-Press Symbols */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.4 }}
              className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-6 border border-pink-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <Keyboard className="w-6 h-6" style={{ color: '#FF6B9D' }} />
                <h3 className="font-semibold text-lg" style={{ color: '#0A0A0A' }}>Long-Press Symbols</h3>
              </div>
              <div className="bg-white rounded-xl p-4 mb-3">
                <div className="flex items-center gap-4 justify-center font-mono text-lg">
                  <span><strong>Q</strong> → 1</span>
                  <span><strong>A</strong> → @</span>
                  <span><strong>S</strong> → #</span>
                  <span><strong>D</strong> → $</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Hold any key <strong>400ms</strong> to type the hint symbol
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MagicShortcuts;
